import { useEffect, useRef } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Style, Icon } from 'ol/style';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import 'ol/ol.css';

// Default center on Hawaii
const DEFAULT_CENTER = [-155.5, 19.9];
const DEFAULT_ZOOM = 8;

const isValidCoordinate = (lon, lat) =>
    lon !== '' &&
    lat !== '' &&
    !isNaN(lon) &&
    !isNaN(lat) &&
    lon >= -180 &&
    lon <= 180 &&
    lat >= -90 &&
    lat <= 90;

export default function MapPinPicker({ longitude, latitude, onChange, height = 300 }) {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerSourceRef = useRef(null);
    const fromMapClickRef = useRef(false);

    useEffect(() => {
        if (!mapRef.current || mapInstanceRef.current) {
            return;
        }

        // Create marker source
        const markerSource = new VectorSource();
        markerSourceRef.current = markerSource;

        // Create marker layer with a simple circle style
        const markerLayer = new VectorLayer({
            source: markerSource,
            style: new Style({
                image: new Icon({
                    anchor: [0.5, 1],
                    src:
                        'data:image/svg+xml;charset=utf-8,' +
                        encodeURIComponent(`
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="36" viewBox="0 0 24 36">
                            <path fill="#e53935" d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24c0-6.6-5.4-12-12-12zm0 16c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z"/>
                        </svg>
                    `)
                })
            })
        });

        // Determine initial center
        const hasValidCoords = isValidCoordinate(longitude, latitude);
        const center = hasValidCoords
            ? fromLonLat([parseFloat(longitude), parseFloat(latitude)])
            : fromLonLat(DEFAULT_CENTER);

        // Create map
        const map = new Map({
            target: mapRef.current,
            layers: [
                new TileLayer({
                    source: new OSM()
                }),
                markerLayer
            ],
            view: new View({
                center,
                zoom: hasValidCoords ? 14 : DEFAULT_ZOOM
            })
        });

        // Add initial marker if coordinates exist
        if (hasValidCoords) {
            const marker = new Feature({
                geometry: new Point(center)
            });
            markerSource.addFeature(marker);
        }

        // Handle map clicks
        map.on('click', evt => {
            const coords = toLonLat(evt.coordinate);
            const [lon, lat] = coords;

            // Update marker
            markerSource.clear();
            const marker = new Feature({
                geometry: new Point(evt.coordinate)
            });
            markerSource.addFeature(marker);

            // Call onChange with new coordinates
            if (onChange) {
                fromMapClickRef.current = true;
                onChange(lon.toFixed(7), lat.toFixed(7));
            }
        });

        mapInstanceRef.current = map;

        return () => {
            map.setTarget(null);
            mapInstanceRef.current = null;
        };
    }, []);

    // Update marker and pan map when coordinates change externally
    useEffect(() => {
        if (!markerSourceRef.current || !mapInstanceRef.current) {
            return;
        }

        // Skip if change originated from map click
        if (fromMapClickRef.current) {
            fromMapClickRef.current = false;
            return;
        }

        if (isValidCoordinate(longitude, latitude)) {
            const coords = fromLonLat([parseFloat(longitude), parseFloat(latitude)]);
            markerSourceRef.current.clear();
            const marker = new Feature({
                geometry: new Point(coords)
            });
            markerSourceRef.current.addFeature(marker);

            // Pan map to show the new location
            mapInstanceRef.current.getView().animate({
                center: coords,
                zoom: 14,
                duration: 500
            });
        }
    }, [longitude, latitude]);

    return (
        <div
            ref={mapRef}
            style={{
                width: '100%',
                height,
                borderRadius: 4,
                overflow: 'hidden',
                border: '1px solid #ccc'
            }}
        />
    );
}
